import { Component, OnInit, inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, finalize, map, catchError } from 'rxjs/operators';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/field.service';
import { Region, Crop } from '@6grain/masofa-monolith-api-client/dictionaries';
import { Field } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/field';
import { FilterOperator } from '@root/src/app/utils/filter-operator';
import { BackButton } from '@components/ui/back-button/back-button';
import { RegionTreeSelectComponent } from '@root/src/app/pages/Ui-Kit/region-tree-select/region-tree-select.component';
import { GenericSelectorComponent } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.component';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { TableModule } from 'primeng/table';
import { ServerTranslateService } from '@app/services/server-translate.service';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { GenericSelectorConfig } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.models';
import { buildFilterQuery } from '@app/utils/_msf';
import { SortType } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {
  CropYieldForecast,
  CropYieldForecastBaseGetQuery,
  CropYieldForecastService,
} from '@6grain/masofa-monolith-api-client/ai-inference';
import { CustomPaginatorComponent } from '@root/src/app/pages/Ui-Kit/custom-paginator/custom-paginator.component';
import { PaginatorState } from 'primeng/paginator';
import { Router } from '@angular/router';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';

@Component({
  selector: 'app-crop-yield-forecast-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    BackButton,
    TableModule,
    RegionTreeSelectComponent,
    GenericSelectorComponent,
    DatePickerComponent,
    ServerTranslatePipe,
    CustomPaginatorComponent,
  ],
  providers: [DatePipe],
  templateUrl: './crop-yield-forecast-list.component.html',
  styleUrls: ['./crop-yield-forecast-list.component.scss'],
})
export class CropYieldForecastListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly forecastService = inject(CropYieldForecastService);
  private readonly regionService = inject(RegionService);
  private readonly cropService = inject(CropService);
  private readonly fieldService = inject(FieldService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly datePipe = inject(DatePipe);
  private readonly router = inject(Router);
  private readonly regionManagerService = inject(RegionManagerService);

  forecasts: CropYieldForecast[] = [];
  regions: Region[] = [];
  cropsDictionary: Crop[] = [];
  fieldsDictionary: Map<string, string> = new Map();

  isLoading = false;
  totalCount = 0;
  pageSize = 20;
  first = 0;

  filters = {
    regionId: null as string | null,
    cropId: null as string | null,
    fieldId: null as string | null,
    startDate: null as Date | null,
    endDate: null as Date | null,
  };

  cropSelectorConfig: GenericSelectorConfig<Crop> = {
    searchField: 'Names',
    displayField: 'names',
    idField: 'id',
    formatLabel: (item) => (this.serverTranslate.translate(item, 'names') as string) || '',
    getItems: (params) =>
      this.cropService
        .dictionariesCropGetByQueryPost({
          offset: params.offset,
          take: params.take,
          filters: params.filters,
        })
        .pipe(
          map((items) =>
            items.sort((a, b) => {
              const nameA = (this.serverTranslate.translate(a, 'names') as string) || '';
              const nameB = (this.serverTranslate.translate(b, 'names') as string) || '';
              return nameA.localeCompare(nameB);
            }),
          ),
        ),
    getTotalCount: (params) => this.cropService.dictionariesCropGetTotalCountPost({ filters: params.filters }),
    getItemById: (id) => this.cropService.dictionariesCropGetByIdIdGet(id),
  };

  fieldSelectorConfig: GenericSelectorConfig<Field> = {
    searchField: 'Name',
    displayField: 'name',
    idField: 'id',
    getItems: (params) =>
      this.fieldService.cropMonitoringFieldGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: params.filters,
        sortBy: 'Name',
        sort: SortType.NUMBER_0,
      }),
    getTotalCount: (params) => this.fieldService.cropMonitoringFieldGetTotalCountPost({ filters: params.filters }),
  };

  constructor() {}

  ngOnInit(): void {
    this.initDictionaries();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initDictionaries(): void {
    this.regionService.dictionariesRegionGetByQueryPost({}).subscribe((res) => {
      this.regions = res || [];
    });
    this.cropService.dictionariesCropGetByQueryPost({ take: 999 }).subscribe((res) => {
      this.cropsDictionary = res || [];
    });
  }

  navigateToField(fieldId: string | undefined): void {
    if (fieldId) {
      this.router.navigate([`/electronic-field-map/fields/${fieldId}/general`]);
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;

    const queryFilters: any = {};
    if (this.filters.regionId) queryFilters['regionId'] = this.filters.regionId;
    if (this.filters.cropId) queryFilters['CropId'] = this.filters.cropId;
    if (this.filters.fieldId) queryFilters['FieldId'] = this.filters.fieldId;
    const searchFilters: any[] = buildFilterQuery(queryFilters);
    await this.regionManagerService.includeChildInRequest(
      { regionId: this.filters.regionId },
      searchFilters as any,
    );
    if (this.filters.startDate) {
      searchFilters.push({
        filterField: 'ForecastDate',
        filterOperator: FilterOperator.GreaterThanOrEqual,
        filterValue: this.datePipe.transform(this.filters.startDate, 'yyyy-MM-dd'),
      });
    }
    if (this.filters.endDate) {
      searchFilters.push({
        filterField: 'ForecastDate',
        filterOperator: FilterOperator.LessThanOrEqual,
        filterValue: this.datePipe.transform(this.filters.endDate, 'yyyy-MM-dd'),
      });
    }

    const query: CropYieldForecastBaseGetQuery = {
      offset: this.first,
      take: this.pageSize,
      filters: searchFilters,
      sortBy: 'CreateAt',
      sort: SortType.NUMBER_1,
    };
    forkJoin({
      data: this.forecastService.aiInferenceCropYieldForecastGetByQueryPost(query),
      count: this.forecastService.aiInferenceCropYieldForecastGetTotalCountPost({ filters: searchFilters }),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.forecasts = res.data || [];
          this.totalCount = res.count;
          this.resolveFieldNames(this.forecasts);
        },
        error: (err) => console.error('Error loading forecasts', err),
      });
  }

  private resolveFieldNames(items: CropYieldForecast[]): void {
    const missingIds = items
      .map((i) => i.fieldId)
      .filter((id) => id && !this.fieldsDictionary.has(id) && !this.fieldsDictionary.has(id.toLowerCase())) as string[];

    const uniqueIds = [...new Set(missingIds)];

    if (uniqueIds.length === 0) return;

    const requests = uniqueIds.map((id) =>
      this.fieldService.cropMonitoringFieldGetByIdIdGet(id).pipe(
        catchError((err) => {
          console.warn(`Failed to load field ${id}`, err);
          return of(null);
        }),
      ),
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((responses) => {
        responses.forEach((response: any) => {
          if (!response) return;

          let fieldData = response;

          if (fieldData.entity) {
            fieldData = fieldData.entity;
          }

          const id = fieldData.id || fieldData.Id;
          const name = fieldData.name || fieldData.Name;

          if (id) {
            const displayName = name || 'Unknown Field';

            this.fieldsDictionary.set(id, displayName);
            this.fieldsDictionary.set(id.toLowerCase(), displayName);
          } else {
            console.warn('Field ID not found in response:', response);
          }
        });

        this.cdr.markForCheck();
      });
  }

  getFieldName(fieldId: string | undefined): string {
    if (!fieldId) return '-';
    return this.fieldsDictionary.get(fieldId) || this.fieldsDictionary.get(fieldId.toLowerCase()) || fieldId;
  }

  getCropById(id: string | undefined): Crop | undefined {
    return this.cropsDictionary.find((c) => c.id === id);
  }

  getRegionDisplayInfo(regionId: string | undefined): { region: Region | null; district: Region | null } {
    if (!regionId || !this.regions.length) {
      return { region: null, district: null };
    }
    const currentRegion = this.regions.find((r) => r.id === regionId);
    if (!currentRegion) {
      return { region: null, district: null };
    }
    if (currentRegion.parentId && currentRegion.parentId !== currentRegion.id) {
      const parentRegion = this.regions.find((r) => r.id === currentRegion.parentId);
      if (parentRegion) {
        return { region: parentRegion, district: currentRegion };
      }
    }
    return { region: currentRegion, district: null };
  }

  onRegionSelected(regionId: string | null): void {
    this.filters.regionId = regionId;
  }

  applyFilters(): void {
    this.first = 0;
    this.loadData();
  }

  resetFilters(): void {
    this.filters = {
      regionId: null,
      cropId: null,
      fieldId: null,
      startDate: null,
      endDate: null,
    };
    this.applyFilters();
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first || 0;
    this.loadData();
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.first = 0;
    this.loadData();
  }
}
